import { Minus, Plus, Trash2 } from 'lucide-react';
import styled from 'styled-components';

import { BasicInput } from 'components/BasicInput';

import { formatEuroAmount } from 'helpers/price';

import { computeMinimums, type MinimumOrderAmountInfo } from './minimums';
import type { CartLine } from './types';

export const CartPanel = ({
    lines,
    minimumOrderAmountInfo,
    onQuantityChange,
}: {
    lines: CartLine[];
    minimumOrderAmountInfo: MinimumOrderAmountInfo;
    onQuantityChange: (productId: string, quantity: number) => void;
}) => {
    const { total, byFlow } = computeMinimums(lines, minimumOrderAmountInfo);

    return (
        <Panel>
            <PanelTitle>
                Panier
                <LineCount>
                    {lines.length} produit{lines.length > 1 ? 's' : ''}
                </LineCount>
            </PanelTitle>
            <Lines>
                {lines.length === 0 ? (
                    <EmptyState>Aucun produit. Choisissez-les dans le catalogue à gauche.</EmptyState>
                ) : (
                    lines.map((line) => (
                        <Line key={line.productId} data-test={`cart-line-${line.productId}`}>
                            <LineInfo>
                                <LineName>{line.name}</LineName>
                                <LineDetail>
                                    {formatEuroAmount(line.unitPrice)} / unité
                                    {line.step > 1 ? ` · par ${line.step}` : ''}
                                </LineDetail>
                                {line.warning ? <LineWarning>{line.warning}</LineWarning> : null}
                            </LineInfo>
                            <Stepper>
                                <StepButton
                                    type="button"
                                    title="Retirer une unité"
                                    onClick={() => onQuantityChange(line.productId, line.quantity - line.step)}
                                >
                                    <Minus size={14} />
                                </StepButton>
                                <QuantityInput
                                    type="number"
                                    min={0}
                                    value={String(line.quantity)}
                                    onChange={(event) =>
                                        onQuantityChange(line.productId, Number(event.target.value) || 0)
                                    }
                                    data-test={`cart-quantity-${line.productId}`}
                                />
                                <StepButton
                                    type="button"
                                    title="Ajouter une unité"
                                    onClick={() => onQuantityChange(line.productId, line.quantity + line.step)}
                                >
                                    <Plus size={14} />
                                </StepButton>
                                <StepButton
                                    type="button"
                                    title="Retirer du panier"
                                    onClick={() => onQuantityChange(line.productId, 0)}
                                >
                                    <Trash2 size={14} />
                                </StepButton>
                            </Stepper>
                            <LineTotal>{formatEuroAmount(line.quantity * line.unitPrice)}</LineTotal>
                        </Line>
                    ))
                )}
            </Lines>
            <Minimums>
                <MinimumRow $isReached={total.isReached}>
                    <span>Sous-total HT</span>
                    <span>
                        {formatEuroAmount(total.amount)}
                        {total.minimum ? ` / ${formatEuroAmount(total.minimum)} mini` : ''}
                    </span>
                </MinimumRow>
                {byFlow.map((minimum) => (
                    <MinimumRow key={minimum.label} $isReached={minimum.isReached}>
                        <span>{minimum.label}</span>
                        <span>
                            {formatEuroAmount(minimum.amount)} / {formatEuroAmount(minimum.minimum)} mini
                        </span>
                    </MinimumRow>
                ))}
            </Minimums>
        </Panel>
    );
};

const Panel = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 0;
    border-left: 1px solid ${({ theme }) => theme.lightBorderColor};
    padding-left: 15px;
`;

const PanelTitle = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    font-weight: 700;
    margin-bottom: 10px;
`;

const LineCount = styled.span`
    color: ${({ theme }) => theme.textColor};
    font-size: 13px;
    font-weight: 400;
`;

const Lines = styled.div`
    flex: 1;
    overflow-y: auto;
    min-height: 0;
`;

const EmptyState = styled.div`
    color: ${({ theme }) => theme.infoTextColor};
    font-size: 13px;
    padding: 20px 0;
`;

const Line = styled.div`
    display: grid;
    grid-template-columns: 1fr auto auto;
    align-items: center;
    gap: 10px;
    padding: 8px 0;

    & + & {
        border-top: 1px solid ${({ theme }) => theme.lightBorderColor};
    }
`;

const LineInfo = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
`;

const LineName = styled.span`
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const LineDetail = styled.span`
    color: ${({ theme }) => theme.infoTextColor};
    font-size: 12px;
`;

const LineWarning = styled.span`
    color: ${({ theme }) => theme.warningColor};
    font-size: 12px;
`;

const Stepper = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;

const StepButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-radius: 4px;
    background: none;
    color: inherit;
    cursor: pointer;
`;

const QuantityInput = styled(BasicInput)`
    width: 60px;
    text-align: center;
`;

const LineTotal = styled.span`
    font-weight: 600;
    white-space: nowrap;
`;

const Minimums = styled.div`
    border-top: 1px solid ${({ theme }) => theme.lightBorderColor};
    padding-top: 10px;
    margin-top: 10px;
`;

const MinimumRow = styled.div<{ $isReached: boolean }>`
    display: flex;
    justify-content: space-between;
    font-size: 13px;
    padding: 2px 0;
    color: ${({ theme, $isReached }) => ($isReached ? theme.successColor : theme.warningColor)};
`;
